import { Observable } from 'rxjs';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { FlowCitation } from './chat.flow.service';

export type SseEvent =
    | { type: 'token'; delta: string }
    | { type: 'step_reset' }
    | { type: 'done'; messageId: number; citations: FlowCitation[] }
    | { type: 'error'; error: string }
    | { type: 'tool_start'; toolName: string; label: string }
    | { type: 'tool_end'; toolName: string };

/** Thrown (and surfaced as an Observable error) when the server returns a non-2xx status. */
export class HttpStatusError extends Error {
    constructor(public readonly status: number) {
        super(`HTTP ${status}`);
        Object.setPrototypeOf(this, HttpStatusError.prototype);
    }
}

/**
 * Parse a raw SSE message into a typed SseEvent.
 * Exported as a pure function so it can be unit-tested independently.
 */
export function parseSseMessage(eventType: string, rawData: string): SseEvent | null {
    try {
        const data = JSON.parse(rawData);
        if (eventType === 'token') { return { type: 'token', delta: data }; }
        if (eventType === 'step_reset') { return { type: 'step_reset' }; }
        if (eventType === 'done') { return { type: 'done', messageId: data.messageId, citations: data.citations ?? [] }; }
        if (eventType === 'error') { return { type: 'error', error: data.error }; }
        if (eventType === 'tool_start') { return { type: 'tool_start', toolName: data.toolName, label: data.label }; }
        if (eventType === 'tool_end') { return { type: 'tool_end', toolName: data.toolName }; }
        return null;
    } catch {
        return null;
    }
}

/**
 * Open an SSE stream via POST and return an Observable of typed events.
 * Unsubscribing aborts the connection, which signals the server to stop.
 */
export function streamChat(
    url: string,
    body: object,
    headers: Record<string, string>
): Observable<SseEvent> {
    return new Observable(observer => {
        const controller = new AbortController();

        fetchEventSource(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            body: JSON.stringify(body),
            signal: controller.signal,
            async onopen(response) {
                if (!response.ok) {
                    throw new HttpStatusError(response.status);
                }
            },
            onmessage(ev) {
const event = parseSseMessage(ev.event, ev.data);
                if (!event) { return; }
                observer.next(event);
                if (event.type === 'done' || event.type === 'error') {
                    observer.complete();
                }
            },
            onerror(err) {
                observer.error(err);
                throw err; // prevent fetchEventSource from retrying
            },
            onclose() {
                observer.complete();
            },
        });

        return () => controller.abort();
    });
}
